import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  UserCredential
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { addUser, auth, db } from '../services/firebase';
import { useYahooAuth } from './YahooAuthContext';

interface AuthContextType {
  user: User | null;
  userRole: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<UserCredential>;
  signup: (email: string, password: string, role: string) => Promise<void>;
  logout: () => Promise<void>;
  getDashboardRoute: () => string;
  isCommissioner: (leagueId: string) => Promise<boolean>;
  yahooIsAuthenticated: boolean;
  yahooLogin: (leagueId: string) => void;
  yahooLogout: (leagueId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { yahooIsAuthenticated, yahooLogin, yahooLogout } = useYahooAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('firebaseuser',firebaseUser)
      setUser(firebaseUser);
      if (firebaseUser) {
        let provider = firebaseUser.providerData[0].providerId === 'google.com' ? 'google' : 'password';
        try {
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            const role = userDoc.data()?.role || null;
            setUserRole(role);
          } else {
            if (provider === 'google'){
              const createUser = await addUser({
                uid: firebaseUser.uid,
                email: firebaseUser.email ? firebaseUser.email : '',
                role: "user",
                verified: true,
                name: firebaseUser.displayName ? firebaseUser.displayName : '',
                photoURL: firebaseUser.photoURL ? firebaseUser.photoURL : '',
              })
              setUserRole("user");
            }
            
          }
          
        } catch (error) {
          console.error("Error fetching user role:", error);
        }
      } else {
        setUserRole(null);
      }
      setIsLoading(false);
    });
  
    return unsubscribe;
  }, []);

  const login = async (email: string, password: string): Promise<UserCredential> => {
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const signedInUser = userCredential.user;

      if (signedInUser) {
        const userDoc = await getDoc(doc(db, 'users', signedInUser.uid));
        const role = userDoc.data()?.role || null;
        setUserRole(role);
      }

      return userCredential;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, role: string) => {
    setIsLoading(true);
    try {
      const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
      if (newUser) {
        await setDoc(doc(db, 'users', newUser.uid), { role });
        setUserRole(role);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await signOut(auth);
      setUserRole(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getDashboardRoute = () => {
    if (userRole === 'admin') {
      return '/admin';
    } else if (userRole === 'commissioner') {
      return '/authHome';
    } else if (userRole === 'user') {
      return '/dashboard';
    } else {
      return '/';
    }
  };

  const isCommissioner = async (leagueId: string): Promise<boolean> => {
    if (!user) return false;
    const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
    const leagueData = leagueDoc.data();
    return leagueData?.commissioners?.includes(user.uid) || false;
  };

  const value: AuthContextType = {
    user,
    userRole,
    isLoading,
    login,
    signup,
    logout,
    getDashboardRoute,
    isCommissioner,
    yahooIsAuthenticated,
    yahooLogin,
    yahooLogout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
